import React, { useState, useRef } from "react";
import {
  Grid,
  Typography,
  Paper,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import apiService from "../../../../ApiService";
import { API_ENDPOINTS } from "../../../../ApiService";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../../pages/authentification/UserContext";

import { TOKEN_KEY } from "../../../services/security";
import PersonalInfo from "./formulaireBreakingParts/PersonalInfo";
import BodyComposition from "./formulaireBreakingParts/BodyComposition";
import FitnessInfo from "./formulaireBreakingParts/FitnessInfo";
import GoalsAndLifestyle from "./formulaireBreakingParts/GoalsAndLifestyle";
import ActivityAndStress from "./formulaireBreakingParts/ActivityAndStress";
import Measurements from "./formulaireBreakingParts/Measurements";
import Equipment from "./formulaireBreakingParts/Equipment";
import PhotosSection from "./formulaireBreakingParts/PhotosSection";
import SumUp from "./formulaireBreakingParts/SumUp";

const FormulaireDebutCoaching = () => {
  const navigate = useNavigate();
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [currentStep, setCurrentStep] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // Adjust based on screen size
  const formRef = useRef(null); // Create a ref for the form container
  const { user, setUser } = useUser();

  const [formData, setFormData] = useState({
    prenom: "",
    nom: "",
    age: "",
    taille: "",
    datedenaissance: "",
    poidsActuel: "",
    gender: "",
    pourcentageGraisseCorporelle: false,
    pourcentageMasseGrasse: "",
    masseGrasseOutil: "",
    musculationQuestion: false,
    muscuAnneeExperience: "",
    muscuDeveloppeCouche: "",
    muscuSquat: "",
    muscuTractions: "",
    muscuPressOverhead: "",
    muscuAutreActivite: false,
    muscuListeAutreActivite: "",
    muscuCommencerAutreActivite: "",
    noMuscuAutreActivite: false,
    noMuscuListeAutreActivite: "",
    noMuscuSouhaitCommencerAutreActivite: "",
    noMuscuSouhaitCommencerMuscu: "",
    objectifsFitness: "",
    frequenceEntrainement: "",
    profession: "",
    pathologiesBlessures: "",
    regimeSpecial: "",
    substancesPED: "",
    entrainementImpossibleMoments: "",
    niveauActivite: "",
    niveauStress: "",
    rythmeSommeil: "",
    cafeine: "",
    niveauEngagement: "",
    tourDePoignet: "",
    tourDeCheville: "",
    photoMain: "",
    minimumPoidsDisquesDeBarre: "",
    minimumHalteres: "",
    rackSquat: false,
    bancHyperextension: false,
    reverseHyperMachine: false,
    gluteHamRaise: false,
    standingCalfRaise: false,
    ceintureDipTraction: false,
    legCurlMachine: "",
    extensionJambes: false,
    paireDeGenouilleres: false,
    seatedCalfRaise: false,
    trx: false,
    sanglePowerlifting: false,
    chainesPowerlifting: false,
    tourACables: false,
    supplementsListes: "",
    planAlimentaireActuel: "",
    planEntrainementActuel: "",
    partagePhoto: false,
    photoDeFace: "",
    photoDeCote: "",
    photoDeDos: "",
  });

  const steps = [
    "Informations Personnelles",
    "Composition Corporelle",
    "Condition Physique",
    "Objectifs et Style de Vie",
    "Activité et Stress",
    "Mesures",
    "Équipement",
    "Photos",
    "Résumé",
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleScrollToTop = () => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
      handleScrollToTop(); // Scroll to the top for better UX
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => prev - 1);
    handleScrollToTop(); // Scroll to the top
  };

  const handleStepClick = (index) => {
    if (index >= 0 && index <= steps.length - 1) {
      setCurrentStep(index);
      handleScrollToTop(); // Ensure the user is scrolled to the top of the form
    }
  };

  const getUserIdFromStorage = () => {
    return localStorage.getItem("idUser");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Form submitted:", formData);

    const idUser = user?.id;
    if (!idUser) {
      alert("User not authenticated.");
      return;
    }

    const formDataToSend = new FormData();
    const formJson = JSON.stringify({
      prenom: formData.prenom,
      nom: formData.nom,
      age: formData.age,
      taille: formData.taille,
      datedenaissance: formData.datedenaissance,
      poidsActuel: formData.poidsActuel,
      gender: formData.gender,
      pourcentageGraisseCorporelle: formData.pourcentageGraisseCorporelle,
      pourcentageMasseGrasse: formData.pourcentageMasseGrasse,
      masseGrasseOutil: formData.masseGrasseOutil,
      musculationQuestion: formData.musculationQuestion,
      muscuAnneeExperience: formData.muscuAnneeExperience,
      muscuDeveloppeCouche: formData.muscuDeveloppeCouche,
      muscuSquat: formData.muscuSquat,
      muscuTractions: formData.muscuTractions,
      muscuPressOverhead: formData.muscuPressOverhead,
      muscuAutreActivite: formData.muscuAutreActivite,
      muscuListeAutreActivite: formData.muscuListeAutreActivite,
      muscuCommencerAutreActivite: formData.muscuCommencerAutreActivite,
      noMuscuAutreActivite: formData.noMuscuAutreActivite,
      noMuscuListeAutreActivite: formData.noMuscuListeAutreActivite,
      noMuscuSouhaitCommencerAutreActivite:
        formData.noMuscuSouhaitCommencerAutreActivite,
      noMuscuSouhaitCommencerMuscu: formData.noMuscuSouhaitCommencerMuscu,
      objectifsFitness: formData.objectifsFitness,
      frequenceEntrainement: formData.frequenceEntrainement,
      profession: formData.profession,
      pathologiesBlessures: formData.pathologiesBlessures,
      regimeSpecial: formData.regimeSpecial,
      substancesPED: formData.substancesPED,
      entrainementImpossibleMoments: formData.entrainementImpossibleMoments,
      niveauActivite: formData.niveauActivite,
      niveauStress: formData.niveauStress,
      rythmeSommeil: formData.rythmeSommeil,
      cafeine: formData.cafeine,
      niveauEngagement: formData.niveauEngagement,
      tourDePoignet: formData.tourDePoignet,
      tourDeCheville: formData.tourDeCheville,
      minimumPoidsDisquesDeBarre: formData.minimumPoidsDisquesDeBarre,
      minimumHalteres: formData.minimumHalteres,
      rackSquat: formData.rackSquat,
      bancHyperextension: formData.bancHyperextension,
      reverseHyperMachine: formData.reverseHyperMachine,
      gluteHamRaise: formData.gluteHamRaise,
      standingCalfRaise: formData.standingCalfRaise,
      ceintureDipTraction: formData.ceintureDipTraction,
      legCurlMachine: formData.legCurlMachine,
      extensionJambes: formData.extensionJambes,
      paireDeGenouilleres: formData.paireDeGenouilleres,
      seatedCalfRaise: formData.seatedCalfRaise,
      trx: formData.trx,
      sanglePowerlifting: formData.sanglePowerlifting,
      chainesPowerlifting: formData.chainesPowerlifting,
      tourACables: formData.tourACables,
      supplementsListes: formData.supplementsListes,
      planAlimentaireActuel: formData.planAlimentaireActuel,
      planEntrainementActuel: formData.planEntrainementActuel,
      partagePhoto: formData.partagePhoto,
      idUser: idUser, // Add idUser dynamically
    });

    formDataToSend.append("form", formJson);

    // Append images only if they exist
    if (formData.photoMain)
      formDataToSend.append("photoMain", formData.photoMain);
    if (formData.photoDeFace)
      formDataToSend.append("photoDeFace", formData.photoDeFace);
    if (formData.photoDeCote)
      formDataToSend.append("photoDeCote", formData.photoDeCote);
    if (formData.photoDeDos)
      formDataToSend.append("photoDeDos", formData.photoDeDos);

    try {
      const response = await apiService.post(
        API_ENDPOINTS.FORMULAIRE_DEBUT_COACHING,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200) {
        alert("Formulaire bien envoyé !");
        // Assume form submission is successful
        const updatedUser = { ...user, hasCompletedForm: true };
        setUser(updatedUser);
        localStorage.setItem(TOKEN_KEY, JSON.stringify(updatedUser));

        // Redirect to the dashboard using the clientID from the API response.
        navigate(`/dashboard/${response.data.clientID}`);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Une erreur est survenue lors de la soumission du formulaire.");
    }

    console.log("Submitting data to the API...");
  };

  const handleBooleanChange = (e) => {
    const { name, value } = e.target;
    // Convert string "true"/"false" to boolean true/false
    const booleanValue = value === "true";

    setFormData((prevData) => ({
      ...prevData,
      [name]: booleanValue,
    }));
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <PersonalInfo
            formData={formData}
            handleInputChange={handleInputChange}
            handleBooleanChange={handleBooleanChange}
          />
        );
      case 1:
        return (
          <BodyComposition
            formData={formData}
            handleInputChange={handleInputChange}
            handleBooleanChange={handleBooleanChange}
          />
        );
      case 2:
        return (
          <FitnessInfo
            formData={formData}
            handleInputChange={handleInputChange}
            handleBooleanChange={handleBooleanChange}
          />
        );
      case 3:
        return (
          <GoalsAndLifestyle
            formData={formData}
            handleInputChange={handleInputChange}
            handleBooleanChange={handleBooleanChange}
          />
        );
      case 4:
        return (
          <ActivityAndStress
            formData={formData}
            handleInputChange={handleInputChange}
            handleBooleanChange={handleBooleanChange}
          />
        );
      case 5:
        return (
          <Measurements
            formData={formData}
            handleInputChange={handleInputChange}
            handleBooleanChange={handleBooleanChange}
          />
        );
      case 6:
        return (
          <Equipment
            formData={formData}
            handleInputChange={handleInputChange}
            handleBooleanChange={handleBooleanChange}
          />
        );
      case 7:
        return (
          <PhotosSection
            formData={formData}
            handleInputChange={handleInputChange}
            handleBooleanChange={handleBooleanChange}
          />
        );
      case 8:
        return <SumUp formData={formData} />;
      default:
        return <Typography variant="h6">Étape inconnue</Typography>;
    }
  };

  return (
    <Box
      ref={formRef} // Attach the ref to the Paper
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        padding: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          maxWidth: "1200px", // Max width for larger screens
          minWidth: isMobile ? "300px" : "600px", // Adjust minWidth dynamically
          padding: "2rem",
          margin: "auto",
          overflow: "hidden",
        }}
      >
        {/* Stepper for progress */}
        <Box sx={{ marginBottom: "2rem", overflowX: "auto" }}>
          <Stepper activeStep={currentStep} alternativeLabel>
            {steps.map((label, index) => (
              <Step key={label} onClick={() => handleStepClick(index)}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Step Content */}
        <form
          onSubmit={(e) => {
            e.preventDefault(); // Prevent default form submission
            if (currentStep === steps.length - 1) {
              handleSubmit(); // Call handleSubmit only on the review step
            }
          }}
        >
          <Grid container spacing={3}>
            {renderStepContent(currentStep)}
          </Grid>

          {/* Navigation Buttons */}
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            sx={{ marginTop: "2rem" }}
          >
            <Button
              onClick={handleBack}
              disabled={currentStep === 0}
              style={{ marginRight: "10px" }}
              variant="contained"
              color="secondary"
            >
              Précédent
            </Button>
            {currentStep === steps.length - 1 ? (
              <Button
                onClick={handleSubmit} // Only handleSubmit for the "Soumettre" button
                variant="contained"
                color="primary"
              >
                Soumettre
              </Button>
            ) : (
              <Button onClick={handleNext} variant="contained" color="primary">
                Suivant
              </Button>
            )}
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default FormulaireDebutCoaching;
